import { useState } from "react";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import LockIcon from "@mui/icons-material/Lock";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { RouteSalesTargetBranch, RouteSalesTargetRoute } from "../../types/route-sales-target.type";
import { formatNumber } from "../../utils";
import GridCellInput from "../common/EditableGrid/GridCellInput";
import { GridNavigationProvider } from "../common/EditableGrid/GridNavigationProvider";
import { BODY_ROW_SX, HEAD_ROW_SX, TABLE_SX, stickyCellSx } from "../DailyExpense/tableStyles";
import {
  TargetDraft,
  amountOf,
  cellKeyOf,
  isPastDay,
  missingDayCountOf,
  routeTotalOf,
} from "./routeSalesTargetDraft";
import { EMPTY_TARGET_TEXT, NO_PLAN_TOOLTIP, PAST_DAY_TOOLTIP, WEEKDAY_SHORT } from "./constants";

export interface CellError {
  text: string;
  message: string;
}

interface CellTarget {
  route: RouteSalesTargetRoute;
  dayIndex: number;
}

interface TargetTableProps {
  branches: RouteSalesTargetBranch[];
  draft: TargetDraft;
  errors: Record<string, CellError>;
  today: string;
  onCommit: (route: RouteSalesTargetRoute, dayIndex: number, text: string) => void;
}

const MAX_HEIGHT = 620;
const NOT_PLANNED_BG = "action.hover";
const textOf = (amount: number | null) => (amount === null ? EMPTY_TARGET_TEXT : formatNumber(amount));

/** Routes grouped by branch (collapsible, with branch totals) × the month's days + month total. */
export default function TargetTable({ branches, draft, errors, today, onCommit }: Readonly<TargetTableProps>) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const days = branches[0]?.routes[0]?.days ?? [];
  const toggle = (branchCode: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (!next.delete(branchCode)) next.add(branchCode);
      return next;
    });
  const handleCommit = (target: CellTarget, text: string) => onCommit(target.route, target.dayIndex, text);
  let gridRow = 0;

  return (
    <Paper sx={{ p: 0 }}>
      <TableContainer sx={{ maxHeight: MAX_HEIGHT }}>
        <GridNavigationProvider>
          <Table size="small" stickyHeader sx={TABLE_SX}>
            <TableHead sx={HEAD_ROW_SX}>
              <TableRow>
                <TableCell sx={{ ...stickyCellSx("left"), zIndex: 3, minWidth: 220 }}>สาย</TableCell>
                {days.map((day) => (
                  <TableCell key={day.date} align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {dayjs(day.date).date()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {WEEKDAY_SHORT[dayjs(day.date).day()]}
                      {day.date === today ? " · วันนี้" : ""}
                    </Typography>
                  </TableCell>
                ))}
                <TableCell align="right">รวมทั้งเดือน</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {branches.map((branch) => {
                const isCollapsed = collapsed.has(branch.branch_code);
                return [
                  <TableRow key={branch.branch_code} sx={{ bgcolor: "grey.100" }}>
                    <TableCell sx={{ ...stickyCellSx("left"), fontWeight: 600 }}>
                      <IconButton size="small" aria-label={`พับ ${branch.branch_code}`} onClick={() => toggle(branch.branch_code)}>
                        {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                      {branch.branch_code} {branch.branch_name} · {branch.routes.length} สาย
                    </TableCell>
                    {days.map((day, dayIndex) => (
                      <TableCell key={day.date} align="right" sx={{ fontWeight: 600 }}>
                        {formatNumber(
                          branch.routes.reduce((sum, route) => sum + (amountOf(route, route.days[dayIndex], draft) ?? 0), 0),
                          { decimals: 0 },
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatNumber(
                        branch.routes.reduce((sum, route) => sum + routeTotalOf(route, draft), 0),
                        { decimals: 0 },
                      )}
                    </TableCell>
                  </TableRow>,
                  ...(isCollapsed
                    ? []
                    : branch.routes.map((route) => {
                        const row = gridRow;
                        gridRow += 1;
                        const missing = missingDayCountOf(route, draft, today);
                        return (
                          <TableRow key={route.route_uuid} sx={BODY_ROW_SX}>
                            <TableCell sx={stickyCellSx("left")}>
                              {route.route_code} {route.route_name}
                              {missing > 0 && (
                                <Typography variant="caption" color="warning.main" display="block">
                                  ยังไม่มีเป้า {missing} วัน
                                </Typography>
                              )}
                            </TableCell>
                            {route.days.map((day, dayIndex) => {
                              const key = cellKeyOf(route.route_uuid, day.date);
                              const amount = amountOf(route, day, draft);
                              const background = day.is_planned ? undefined : NOT_PLANNED_BG;
                              if (isPastDay(day.date, today)) {
                                return (
                                  <TableCell key={day.date} align="right" sx={{ bgcolor: "action.disabledBackground", color: "text.disabled" }}>
                                    <Tooltip title={PAST_DAY_TOOLTIP}>
                                      <Box component="span">
                                        {amount === null ? EMPTY_TARGET_TEXT : formatNumber(amount, { decimals: 0 })}
                                        <LockIcon sx={{ fontSize: 11, ml: 0.25 }} />
                                      </Box>
                                    </Tooltip>
                                  </TableCell>
                                );
                              }
                              const error = errors[key];
                              return (
                                <TableCell key={day.date} sx={{ bgcolor: background, p: 0.25 }}>
                                  <GridCellInput<CellTarget>
                                    position={{ row, column: dayIndex }}
                                    target={{ route, dayIndex }}
                                    text={error === undefined ? textOf(amount) : error.text}
                                    onCommit={handleCommit}
                                    ariaLabel={`${route.route_code} วันที่ ${dayIndex + 1}`}
                                    hasError={error !== undefined}
                                    tooltip={error?.message ?? (day.is_planned ? "" : NO_PLAN_TOOLTIP)}
                                    inputSx={{ minWidth: 72 }}
                                  />
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">{formatNumber(routeTotalOf(route, draft), { decimals: 0 })}</TableCell>
                          </TableRow>
                        );
                      })),
                ];
              })}
            </TableBody>
          </Table>
        </GridNavigationProvider>
      </TableContainer>
    </Paper>
  );
}
